import { useCallback, useEffect, useId, useState } from 'react';

export type ApplicationEvents = 'trigger_collapse' | 'kebab_open_event' | 'license_plate_menu_open_event';

export const useEventListener = (eventName: ApplicationEvents, handler: (event: Event) => void) => {
  useEffect(() => {
    window.addEventListener(eventName, handler, false);

    return () => {
      window.removeEventListener(eventName, handler, false);
    };
  }, [handler]);
};

export const emitEvent = (eventName: ApplicationEvents, detail?: any) => {
  const event = new CustomEvent(eventName, { detail });
  window.dispatchEvent(event);
};

export const useExclusiveOpenState = (eventName: ApplicationEvents) => {
  const [isOpen, setIsOpen] = useState(false);
  const instanceId = useId();

  const handleOtherInstanceOpen = useCallback(
    (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.id !== instanceId) {
        setIsOpen(false);
      }
    },
    [instanceId],
  );

  useEventListener(eventName, handleOtherInstanceOpen);

  const setExclusiveOpen = useCallback(
    (nextIsOpen: boolean) => {
      setIsOpen(nextIsOpen);
      if (nextIsOpen) {
        emitEvent(eventName, { id: instanceId });
      }
    },
    [eventName, instanceId],
  );

  return [isOpen, setExclusiveOpen] as const;
};
